import React from "react";
import {betterMemo} from "js/common/utils/more-memo";
import Immutable from "immutable";
import EditKpiRow from "js/admin/kpis/edit-kpis/edit-kpi-row";
import {getNameForValidation, isKpiNameDupe} from "js/admin/kpis/edit-kpis/validation";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const EditKpiEntity = betterMemo(
    {debug: false, displayName: "EditKpiEntity"},
    ({
      index = 2,
      clientHasEdit,
      columnsKpiIdToKpis,
      entityExpanded,
      entityLabel,
      filterText,
      handleDuplicateClick,
      handleInheritClick,
      idToTemplate,
      idToWrappedKpi,
      isCube19User,
      isEditor,
      kpiIdToContributingKpis,
      kpiIdToDependentKpis,
      kpiIds,
      nameToCount,
      onDeleteMetric,
      onDisableMetricToggle,
      onExpandEntityClick,
      onKpiChange,
      onNavigationClick,
      onRevertToLegacyFormat,
      onRowClick,
      showOnlyChangedKpis,
      showOnlyFailingKpis
    }) => {
      const hasKpis = !kpiIds.isEmpty();
      const {theme} = React.useContext(CustomThemeContext);

      const entityStyle = {
        background: theme.palette.background.card,
        boxShadow:
            entityExpanded ?
                "0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13), 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.10)"
                :
                "0px 0px 1.6px 0px rgba(0, 0, 0, 0.1), 0px 0px 0px 0px rgba(0, 0, 0, 0)",
        marginTop: entityExpanded && index !== 0 ? 10 : 0,
        marginBottom: entityExpanded ? 10 : 0
      };

      const entityHeadingContainerStyle = {
        display: "flex",
        padding: 10,
        alignItems: "center",
        cursor: "pointer"
      };

      const entityHeadingStyle = {
        fontSize: 16,
        fontWeight: 600
      };

      const shouldAutoExpandContent = ((filterText && filterText !== "") || showOnlyFailingKpis || showOnlyChangedKpis)
          && hasKpis;
      if (filterText && !hasKpis) {
        return <div />;
      }

      return (
          <div style={entityStyle} key={`entity-${entityLabel}-${index}`}>
            <div style={entityHeadingContainerStyle} onClick={() => onExpandEntityClick(entityLabel, entityExpanded)}>
              <h3 style={entityHeadingStyle}>{entityLabel}</h3>
              <span style={{flexGrow: 1}} />
              {!shouldAutoExpandContent && <i
                  data-test-id={"expand-entity-section"}
                  className={entityExpanded || shouldAutoExpandContent
                      ? "bhi-sort-asc"
                      : "bhi-sort-desc"} />}
            </div>
            {(entityExpanded || shouldAutoExpandContent) &&
                <ul style={{display: "block", padding: "0 10px 15px 10px"}}>
                  {hasKpis ? kpiIds.map((id, index) => {
                    const wrappedKpi = idToWrappedKpi.get(id);
                    const columnsKpiId = wrappedKpi.getIn(["kpi", "columnsKpiId"]) || id;
                    const mirrorColor = idToWrappedKpi.getIn([columnsKpiId, "mirrorColor"]);
                    const combineError = wrappedKpi.get("combineError");
                    const kpisWithMatchingColumns = columnsKpiIdToKpis.get(columnsKpiId, Immutable.List());
                    return <EditKpiRow
                        clientHasEdit={clientHasEdit}
                        combineError={combineError}
                        handleDuplicateClick={handleDuplicateClick}
                        handleInheritClick={handleInheritClick}
                        hasChanged={wrappedKpi.get("changed") || wrappedKpi.get("columnsChanged")}
                        idToTemplate={idToTemplate}
                        isCube19User={isCube19User}
                        isEditor={isEditor}
                        isNameDupe={isKpiNameDupe(getNameForValidation(wrappedKpi.get("kpi")), nameToCount)}
                        key={id}
                        kpi={wrappedKpi.get("kpi")}
                        kpiIdToContributingKpis={kpiIdToContributingKpis}
                        kpiIdToDependentKpis={kpiIdToDependentKpis}
                        kpisWithMatchingColumns={kpisWithMatchingColumns}
                        mirrorColor={mirrorColor}
                        onDeleteMetric={onDeleteMetric}
                        onDisableMetricToggle={onDisableMetricToggle}
                        onKpiChange={onKpiChange}
                        onNavigationClick={onNavigationClick}
                        onRevertToLegacyFormat={onRevertToLegacyFormat}
                        onRowClick={onRowClick}
                        requiresExplanationUpdate={wrappedKpi.get("requiresExplanationUpdate")}
                        submitError={wrappedKpi.get("submitError")}
                        testResult={wrappedKpi.get("testResult")}
                        theme={theme}
                    />;
                  }) : <div style={{fontSize: 12}}>No Metrics to display</div>}
                </ul>}
          </div>
      );
    });

export default EditKpiEntity;